import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { List, Robot, BookmarkSimple } from '@phosphor-icons/react';
import { ChatMessage, ChatInput, SessionSidebar, EmptyState, LocationBanner, SavedSourcesPanel } from '../components/assistant';

const API_URL = (typeof window !== 'undefined' && window.location.hostname && !/^(localhost|127\.0\.0\.1)$/i.test(window.location.hostname)) ? window.location.origin : process.env.REACT_APP_BACKEND_URL;

export default function Assistant() {
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [sending, setSending] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [webSearch, setWebSearch] = useState(false);
  const [sourcesOpen, setSourcesOpen] = useState(false);
  const [savedSourceUrls, setSavedSourceUrls] = useState([]);
  const bottomRef = useRef(null);

  useEffect(() => { fetchSessions(); fetchSavedSourceUrls(); }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchSessions = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/assistant/sessions`, { withCredentials: true });
      setSessions(res.data.sessions || []);
    } catch { /* ignore */ }
  };

  const fetchSavedSourceUrls = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/assistant/sources`, { withCredentials: true });
      setSavedSourceUrls((res.data.sources || []).map(s => s.url));
    } catch { /* ignore */ }
  };

  const loadSession = async (sessionId) => {
    setActiveSession(sessionId);
    try {
      const res = await axios.get(`${API_URL}/api/assistant/history/${sessionId}`, { withCredentials: true });
      setMessages(res.data.messages || []);
    } catch { setMessages([]); }
  };

  const startNewChat = () => {
    setActiveSession(null);
    setMessages([]);
    setSidebarOpen(false);
  };

  const deleteSession = async (sessionId) => {
    try {
      await axios.delete(`${API_URL}/api/assistant/sessions/${sessionId}`, { withCredentials: true });
      setSessions(prev => prev.filter(s => s.session_id !== sessionId));
      if (activeSession === sessionId) startNewChat();
    } catch { /* ignore */ }
  };

  const sendMessage = async (text) => {
    const userMsg = { role: 'user', content: text, ts: new Date().toISOString() };
    setMessages(prev => [...prev, userMsg]);
    setSending(true);

    try {
      const res = await axios.post(`${API_URL}/api/assistant/chat`, {
        message: text,
        session_id: activeSession,
        web_search: webSearch,
      }, { withCredentials: true });

      const assistantMsg = {
        role: 'assistant',
        content: res.data.reply,
        ts: new Date().toISOString(),
        gmail_searched: res.data.gmail_searched,
        gmail_query: res.data.gmail_query,
        web_searched: res.data.web_searched,
        web_query: res.data.web_query,
        actions: res.data.actions || [],
        citations: res.data.citations || [],
      };
      setMessages(prev => [...prev, assistantMsg]);

      if (!activeSession) {
        setActiveSession(res.data.session_id);
      }
      fetchSessions();
    } catch {
      setMessages(prev => [...prev, {
        role: 'assistant', content: "Something went wrong. Try again?", ts: new Date().toISOString()
      }]);
    } finally {
      setSending(false);
    }
  };

  const handleSourceSaved = useCallback((url) => {
    setSavedSourceUrls(prev => [...prev, url]);
  }, []);

  const handleSourceDeleted = useCallback((url) => {
    setSavedSourceUrls(prev => prev.filter(u => u !== url));
  }, []);

  return (
    <Layout>
      <div className="flex h-[calc(100vh-0px)] sm:h-[calc(100vh-0px)]" data-testid="assistant-page">
        {/* Session sidebar */}
        <SessionSidebar
          sessions={sessions}
          activeSession={activeSession}
          onSelect={loadSession}
          onNew={startNewChat}
          onDelete={deleteSession}
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
        />

        {/* Main chat area */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#f2f4f6] bg-white">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setSidebarOpen(true)}
                className="sm:hidden p-2 rounded-lg hover:bg-[#f2f4f6]"
                data-testid="toggle-sessions-btn"
              >
                <List size={18} weight="bold" className="text-[#596065]" />
              </button>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-[#0f172a] flex items-center justify-center">
                  <Robot size={16} className="text-white" weight="duotone" />
                </div>
                <div>
                  <p className="font-bold text-sm text-[#2d3338]">LifeAdmin Assistant</p>
                  <p className="text-[10px] text-[#acb3b8]">Your AI Chief of Staff</p>
                </div>
              </div>
            </div>
            <button
              onClick={() => setSourcesOpen(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-[#596065] hover:bg-[#fef3c7] hover:text-[#92400e] border border-[#e2e8f0] hover:border-[#fcd34d] transition-all"
              data-testid="open-saved-sources"
            >
              <BookmarkSimple size={14} weight="duotone" className="text-[#f59e0b]" />
              Sources
            </button>
          </div>

          {/* Location consent banner */}
          <LocationBanner />

          {/* Messages area */}
          <div className="flex-1 overflow-y-auto px-4 py-6">
            {messages.length === 0 ? (
              <EmptyState onSuggestionClick={sendMessage} />
            ) : (
              <>
                {messages.map((msg, i) => (
                  <ChatMessage
                    key={i}
                    message={msg}
                    savedSourceUrls={savedSourceUrls}
                    onSourceSaved={handleSourceSaved}
                  />
                ))}
                {sending && (
                  <div className="flex justify-start mb-4">
                    <div className="bg-[#f2f4f6] rounded-2xl rounded-bl-md px-4 py-3">
                      <div className="flex gap-1">
                        <span className="w-2 h-2 bg-[#acb3b8] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="w-2 h-2 bg-[#acb3b8] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <span className="w-2 h-2 bg-[#acb3b8] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    </div>
                  </div>
                )}
                <div ref={bottomRef} />
              </>
            )}
          </div>

          {/* Input */}
          <ChatInput onSend={sendMessage} disabled={sending} webSearch={webSearch} onToggleWebSearch={() => setWebSearch(w => !w)} />
        </div>
      </div>

      {/* Saved Sources Panel */}
      <SavedSourcesPanel
        open={sourcesOpen}
        onClose={() => setSourcesOpen(false)}
        onSourceDeleted={handleSourceDeleted}
      />
    </Layout>
  );
}
